import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { cn } from '@/lib/utils';

interface Toast {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'destructive' | 'success';
}

interface ToastContextType {
  toasts: Toast[];
  toast: (title: string, description?: string, variant?: Toast['variant']) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((title: string, description?: string, variant: Toast['variant'] = 'default') => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, title, description, variant }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss }}>
      {children}
      <div className="fixed inset-x-4 top-4 z-[110] flex flex-col gap-2 pointer-events-none sm:inset-x-auto sm:right-5 sm:top-5 sm:w-80">
        {toasts.map(t => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto flex w-full items-start gap-3 rounded-xl border bg-white p-4 shadow-lg dark:bg-neutral-900 dark:border-neutral-800',
              t.variant === 'destructive' && 'border-red-500/50 bg-red-50/50',
              t.variant === 'success' && 'border-emerald-500/50 bg-emerald-50/50'
            )}
          >
            <div className="flex flex-col">
              <h4 className="font-semibold text-neutral-900 dark:text-white">{t.title}</h4>
              {t.description && <p className="text-sm text-neutral-500 dark:text-neutral-400">{t.description}</p>}
            </div>
            <button
              onClick={() => dismiss(t.id)}
              className="absolute top-2 right-2 rounded-md p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
            >
              <span className="sr-only">Dismiss</span>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
}
